import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Landing from "./pages/Landing";
import OrganizerLogin from "./pages/OrganizerLogin";
import SuperAdminLogin from "./pages/SuperAdminLogin";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";
import OrganizerDashboard from "./pages/OrganizerDashboard";
import CreateEvent from "./pages/CreateEvent";
import EventDetail from "./pages/EventDetail";
import QRScanner from "./pages/QRScanner";
import PublicRegistration from "./pages/PublicRegistration";
import Payment from "./pages/Payment";
import PaymentFailed from "./pages/PaymentFailed";
import Confirmation from "./pages/Confirmation";
import EventMode from "./pages/EventMode";
import MyTickets from "./pages/MyTickets";
import PayoutSettings from "./pages/PayoutSettings";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import RefundPolicy from "./pages/RefundPolicy";

const isSuperAdminHost =
  typeof window !== "undefined" && window.location.hostname === "app.growithevent.com";

function PublicRoot() {
  return isSuperAdminHost ? <SuperAdminLogin /> : <Landing />;
}

function SuperAdminHostOnly({ children }) {
  if (!isSuperAdminHost) return <Navigate to="/" replace />;
  return children;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500">
        Loading...
      </div>
    );
  }
  if (!user) return <Navigate to="/organizer/login" replace />;
  return children;
}

function ProtectedSuperAdmin({ children }) {
  const { superAdmin, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500">
        Loading...
      </div>
    );
  }
  if (!superAdmin) return <Navigate to="/super-admin/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <Routes>
            <Route path="/" element={<PublicRoot />} />
            <Route path="/organizer/login" element={<OrganizerLogin />} />
            <Route
              path="/super-admin/login"
              element={
                <SuperAdminHostOnly>
                  <SuperAdminLogin />
                </SuperAdminHostOnly>
              }
            />
            <Route
              path="/super-admin"
              element={
                <SuperAdminHostOnly>
                  <ProtectedSuperAdmin>
                    <SuperAdminDashboard />
                  </ProtectedSuperAdmin>
                </SuperAdminHostOnly>
              }
            />
            <Route
              path="/organizer/dashboard"
              element={
                <Protected>
                  <OrganizerDashboard />
                </Protected>
              }
            />
            <Route
              path="/organizer/payouts"
              element={
                <Protected>
                  <PayoutSettings />
                </Protected>
              }
            />
            <Route
              path="/organizer/events/new"
              element={
                <Protected>
                  <CreateEvent />
                </Protected>
              }
            />
            <Route
              path="/organizer/events/:eventId"
              element={
                <Protected>
                  <EventDetail />
                </Protected>
              }
            />
            <Route
              path="/organizer/events/:eventId/scan"
              element={
                <Protected>
                  <QRScanner />
                </Protected>
              }
            />
            <Route path="/e/:eventId" element={<PublicRegistration />} />
            <Route path="/my-tickets" element={<MyTickets />} />
            <Route path="/pay/:registrationId" element={<Payment />} />
            <Route
              path="/pay/:registrationId/failed"
              element={<PaymentFailed />}
            />
            <Route path="/ticket/:registrationId" element={<Confirmation />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route
              path="/organizer/events/:eventId/live"
              element={
                <Protected>
                  <EventMode />
                </Protected>
              }
            />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
